import { ComparisonTimelineStep } from "../types/timeline";

/**
 * Timeline steps are configured with the backend enum text, so steps that are
 * logged per resource carry placeholders such as `{resource_id}`. The concrete
 * value only exists in the `log_message` of the timeline status response, so it
 * is read back out of that message and substituted into the display copy.
 */

const PLACEHOLDER_SOURCE = "\\{([a-zA-Z_][a-zA-Z0-9_]*)\\}";

const placeholderRegExp = (): RegExp => new RegExp(PLACEHOLDER_SOURCE, "g");

/** Escapes literal text and lets any run of whitespace match any other. */
const escapeTemplateLiteral = (value: string): string =>
  value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&").replace(/(\\?\s)+/g, "\\s+");

export const hasTimelinePlaceholder = (text: string | undefined): boolean =>
  !!text && new RegExp(PLACEHOLDER_SOURCE).test(text);

/** Drops placeholders that could not be resolved, leaving readable copy. */
export const stripTimelinePlaceholders = (text: string): string =>
  text
    .replace(/\s*\{[a-zA-Z_][a-zA-Z0-9_]*\}/g, "")
    .replace(/\s+/g, " ")
    .trim();

/**
 * Turns a message template into a regex that captures each placeholder value,
 * e.g. `"Executed for resource {resource_id}."` captures `4767` out of
 * `"Executed for resource 4767."`. The match is unanchored and case-insensitive
 * so extra context around the backend message does not defeat it.
 */
const buildTemplateMatcher = (
  template: string,
): { regex: RegExp; names: string[] } => {
  const names: string[] = [];
  let pattern = "";
  let lastIndex = 0;
  const placeholders = placeholderRegExp();
  let match = placeholders.exec(template);

  while (match !== null) {
    pattern += escapeTemplateLiteral(template.slice(lastIndex, match.index));
    names.push(match[1]);
    lastIndex = match.index + match[0].length;
    // A trailing placeholder has no literal left to stop a lazy capture.
    pattern += /^[\s.]*$/.test(template.slice(lastIndex)) ? "(.+)" : "(.+?)";
    match = placeholders.exec(template);
  }
  pattern += escapeTemplateLiteral(template.slice(lastIndex));

  return { regex: new RegExp(pattern, "i"), names };
};

const joinDistinct = (values: string[]): string =>
  values.filter((value, index) => values.indexOf(value) === index).join(", ");

export type TimelinePlaceholderValues = Record<
  string,
  string | number | null | undefined
>;

/**
 * Resolves the placeholders of a step's display message. Values are taken from
 * the log messages the backend emitted for that step when they carry them, and
 * otherwise from `placeholderValues`, which the calling screen supplies from
 * the execution it is already showing. Steps logged once per resource produce
 * several values, which are listed together.
 */
export const resolveTimelineDisplayMessage = (
  step: ComparisonTimelineStep,
  logMessages: string[] | undefined,
  placeholderValues?: TimelinePlaceholderValues,
): string => {
  const { displayMessage, title } = step;
  if (!hasTimelinePlaceholder(displayMessage)) return displayMessage;

  const messages = (logMessages || [])
    .map((message) => (message || "").trim())
    .filter(Boolean);

  // The log message follows the backend enum text, which is mirrored by `title`;
  // `displayMessage` is tried as well since the two wordings can converge.
  const matchers = [title, displayMessage]
    .filter(hasTimelinePlaceholder)
    .map(buildTemplateMatcher);
  const values = new Map<string, string[]>();

  messages.forEach((message) => {
    matchers.some(({ regex, names }) => {
      const matched = regex.exec(message);
      if (!matched) return false;
      names.forEach((name, index) => {
        const value = matched[index + 1]?.trim().replace(/[.\s]+$/, "");
        if (!value || hasTimelinePlaceholder(value)) return;
        values.set(name, [...(values.get(name) || []), value]);
      });
      return true;
    });
  });

  const resolved = displayMessage.replace(
    placeholderRegExp(),
    (token, name) => {
      const logged = values.get(name);
      if (logged?.length) return joinDistinct(logged);
      const supplied = placeholderValues?.[name];
      return supplied == null || supplied === "" ? token : String(supplied);
    },
  );

  if (!hasTimelinePlaceholder(resolved)) return resolved;

  // Nothing could fill the placeholder: prefer the backend message when there
  // is one, since it carries more context than the stripped copy.
  const resolvedMessages = messages.filter(
    (message) => !hasTimelinePlaceholder(message),
  );
  return resolvedMessages.length
    ? joinDistinct(resolvedMessages)
    : step.unresolvedDisplayMessage ||
        stripTimelinePlaceholders(displayMessage);
};

/** Groups the timeline status response messages by their step enum key. */
export const groupLogMessagesByEnumKey = (
  response: { log_enum_key: string; log_message: string }[] | undefined,
): Map<string, string[]> => {
  const grouped = new Map<string, string[]>();
  (response || []).forEach((item) => {
    if (!item?.log_enum_key) return;
    const messages = grouped.get(item.log_enum_key) || [];
    messages.push(item.log_message);
    grouped.set(item.log_enum_key, messages);
  });
  return grouped;
};
